// Human-readable changelog surfaced on /changelog.
//
// Each entry is ONE production release, not one day: keyed on the
// production build SHA (7-char) with the release date shown next
// to it. Multiple releases can happen on the same day; each gets
// its own block so a supervisor can see "build XYZ shipped on
// Aug 22 with these five changes."
//
// The "pending" sentinel represents work that's on preview but
// hasn't been promoted to production yet. Only rendered on the
// preview channel — the production bundle only knows about
// releases that had already shipped when it was built.
//
// Write each bullet as if you're describing the update to a
// supervisor scanning the app on their phone — no jargon, no
// filenames, no SHAs. Group related commits into ONE bullet
// where possible. The raw git log is still visible on the same
// page behind a "Technical details" toggle (platform developer
// only), so nothing gets lost.
//
// PROMOTE WORKFLOW: when the user promotes the "pending" block to
// production, replace its `sha: "pending"` with the actual 7-char
// SHA of the merge commit that landed on main, add `date`, and
// start a fresh empty pending block above for the next round.

export type ChangeType = "new" | "improved" | "fixed";

export type ChangelogChange = {
  type: ChangeType;
  title: string;
  detail?: string;
};

export type ChangelogRelease = {
  // 7-char SHA of the production build this release shipped as,
  // OR the literal string "pending" for work that's on preview
  // but not yet promoted to production.
  sha: string;
  // YYYY-MM-DD the build was promoted to production. Omit for
  // pending releases.
  date?: string;
  changes: ChangelogChange[];
};

export const CHANGELOG: ChangelogRelease[] = [
  {
    sha: "pending",
    changes: [
      {
        type: "improved",
        title: "Dark mode: shift / on-call color surfaces now mute to match the dark palette",
        detail:
          "Shift section banners on the schedule, on-call calendar "
          + "assignment cells, the shift-tinted group headers inside "
          + "the Understaffed / Overstaffed / Request-list widgets, "
          + "the Shift Bid sheet's shift header, and the on-call "
          + "today-strip chips were all blasting the raw bright "
          + "shift color via inline backgroundColor — great on "
          + "white, blinding on slate-950. New .themed-fill class "
          + "applies filter: brightness(0.55) saturate(0.75) in "
          + "dark + forces a light text color (so the readable-on-"
          + "light text readableTextOn() computed doesn't vanish on "
          + "the now-dark bg). Color identity (green stays green-"
          + "ish, red red-ish, etc.) is preserved; intensity drops "
          + "to match the muted button palette.",
      },
      {
        type: "improved",
        title: "Dark mode: cell attention wash switched to teal (distinct from pay-period violet)",
        detail:
          "Cells with notes or a partial time-off now paint teal in "
          + "dark mode instead of indigo. The pay-weekend bookends "
          + "stay violet; a cell that happens to be BOTH a pay-"
          + "weekend edge AND has notes reads as two separate hues "
          + "stacked, rather than muddled indigo-on-violet.",
      },
      {
        type: "improved",
        title: "Dark mode: amber global overrides repainted — brown is gone",
        detail:
          "Found the root cause the previous tailwind-level fixes "
          + "couldn't beat: styles.css carried a global dark-mode "
          + "override block that pinned every amber class to a "
          + "brown palette via !important. Rewrote those overrides "
          + "so .bg-amber-50 / .bg-amber-50/60 (pay-weekend "
          + "bookends) paint violet, and .bg-amber-100 (OIC chip, "
          + "OT chip, extended-range chip, awaiting-review pill, "
          + "etc.) paints indigo, with text + border swaps to "
          + "match. No more mud in dark mode — attention surfaces "
          + "stand out as indigo, pay-period edges as violet, and "
          + "the two don't look like each other.",
      },
      {
        type: "improved",
        title: "Dark mode: pay-weekend marker switched to violet (distinct from attention indigo)",
        detail:
          "The pay-period Sun / Sat bookends now paint with "
          + "dark:bg-violet-500/25 — a distinct hue that's not the "
          + "indigo attention wash and not the sky hover highlight, "
          + "so each dark-mode signal reads as its own thing. The "
          + "previous slate-700 lift was too subtle; violet is "
          + "visible without being warm.",
      },
      {
        type: "improved",
        title: "Dark mode: brighter indigo tints + pay-weekend marker fix",
        detail:
          "Follow-up to the amber → indigo swap. Two tweaks: "
          + "\n\n"
          + "1. The dark-mode indigo values were too deep (900 "
          + "base) and read as a dim gray-purple against slate-"
          + "950. Bumped every dark:bg-indigo-900/XX to the "
          + "equivalent dark:bg-indigo-500/XX (lower opacity on a "
          + "brighter base) so the attention surfaces actually "
          + "stand out. Text bumped from indigo-200 to indigo-100. "
          + "\n"
          + "2. The pay-weekend bookend cells use bg-amber-50 in "
          + "cellChrome with no dark variant — that's where the "
          + "residual olive/brown wash was coming from on Sunday / "
          + "Saturday markers. Added a dark:bg-sky-500/10 so pay-"
          + "period edges still read as a soft cue in dark without "
          + "mustard-ing the row.",
      },
      {
        type: "improved",
        title: "Dark mode: Shift Bid toolbar buttons + banners read on a dark background",
        detail:
          "Shift Bid's \"Open for bidding\" / \"Lock\" / \"Live\" / "
          + "\"Locked\" controls were using light-mode (-50 bg + -800 "
          + "text) classes with no dark variants, so in dark mode "
          + "they rendered as blinding light rectangles over the "
          + "slate background. Added dark: variants that drop to "
          + "the matching -900/40 bg + -200 text + -700 border; "
          + "same treatment on the Edit label, error / flash / "
          + "progress banners.",
      },
      {
        type: "improved",
        title: "Notification bell: rows now open a detail popup with a jump link",
        detail:
          "Clicking a notification in the desktop bell dropdown "
          + "used to just mark it read — the truncated body stayed "
          + "truncated. Rows now open a detail popup showing the "
          + "full subject + full body + delivery tags (Email / Push) "
          + "+ an \"Open <page> →\" button routed from the subject "
          + "when it matches a known destination (Shift Bid → the "
          + "Shift Bid page, Coverage request → the dashboard, "
          + "time-off / training → their respective pages, account "
          + "deletion → Account settings). Esc closes; clicking the "
          + "backdrop closes; \"Open →\" navigates.",
      },
      {
        type: "improved",
        title: "Audit log: main row now names what changed in plain English",
        detail:
          "Each entry's target cell picks up a compact summary line "
          + "under the entity name — one \"Field: before → after\" "
          + "per changed key, up to three (with \"+N more\" when a "
          + "bulk edit touched more). UUIDs resolve to entity names "
          + "(e.g. Home shift: Days → Evenings, not two 8-char "
          + "IDs), booleans read as yes/no, weekday arrays print as "
          + "\"Sun, Sat\", empty / null values read as \"(none)\" or "
          + "\"cleared\", and create rows summarize a couple of key "
          + "fields (Type, Status, Partial hours…) so you don't "
          + "have to expand every entry. The full Before / After "
          + "JSON is still one click away in the expanded diff.",
      },
      {
        type: "improved",
        title: "Sidebar: \"beta\" chip on beta-gated entries + banner tightened",
        detail:
          "Beta testers now see a small violet \"beta\" chip on any "
          + "sidebar entry that's still beta-gated (Records + Shift "
          + "Bid today), so it's obvious at a glance which features "
          + "are in test vs shipped. Also: the top-of-page Shift Bid "
          + "live banner is now itself beta-gated so a non-beta "
          + "officer in a beta-testing agency doesn't see the "
          + "nudge and bounce off the route guard.",
      },
      {
        type: "new",
        title: "Shift Bid live: top-of-page banner nudges officers until they pick",
        detail:
          "A shift-bid sheet is now explicitly \"live\" when L2+ "
          + "flips a new \"Open for bidding\" button in the Shift "
          + "Bid page header (migration 0057 adds opened_at). While "
          + "live, every officer in the sheet's department sees a "
          + "sky-blue \"Shift Bid is open for your shift — pick "
          + "your row →\" banner at the top of every page, right "
          + "below the tac banner. The banner hides the moment they "
          + "claim a row, and reappears if a more-senior officer "
          + "bumps them (their claim gets cleared → next fetch "
          + "shows them as unclaimed again). Auto-refetches on "
          + "window focus so a bumped officer returning to the tab "
          + "sees the nudge come back without a reload. Beta-gated "
          + "the same way the Shift Bid page is.",
      },
      {
        type: "improved",
        title: "Shift Bid toggle also hides the Settings → Shift bid tab",
        detail:
          "Disabling Shift Bid in Sections & display now hides the "
          + "existing Settings → Shift bid force-edit tab too, so "
          + "flipping the toggle off cleans up every Shift-Bid "
          + "surface: sidebar entry, route, AND the Settings tab. "
          + "Force-edit path comes back when the toggle flips on.",
      },
      {
        type: "improved",
        title: "Sidebar + Settings: Records/Shift Bid toggles, L2+ gates, On-Call in Schedules",
        detail:
          "Three related tweaks to the nav + permissions surface. "
          + "\n\n"
          + "1. Settings → Sections & display now has toggles for "
          + "Records and Shift Bid alongside the existing Audit / "
          + "Time Off / Training / On-Call switches, so an agency "
          + "can turn either off agency-wide when they're between "
          + "cycles or not using them. "
          + "\n\n"
          + "2. Employees, Shifts, and Records are now L2+ only in "
          + "both the sidebar AND at the route layer. Rank-and-file "
          + "who bookmarked /employees or /shifts bounce back to the "
          + "dashboard instead of a supervisor tool they can't act on. "
          + "\n\n"
          + "3. On-Call moved up into the Schedules section of the "
          + "sidebar (right below the department schedule list and "
          + "\"+ New department\"). Specialty on-call rotations are "
          + "schedule-shaped, so they belong grouped with the other "
          + "schedules instead of floating loose above Settings.",
      },
      {
        type: "improved",
        title: "Settings → Shift Bid: officers grouped by department + home shift",
        detail:
          "The Settings shift-bid form used to render every active "
          + "officer in one flat alphabetical list. Now it groups by "
          + "department (Patrol / Dispatch / Animal Control …) and, "
          + "within each department, by home shift (Days / Evenings "
          + "/ Nights / …) with an Unassigned sub-block for "
          + "officers without a home shift and a top-level Other "
          + "section for officers without a department. Same edit "
          + "cells, just organized so a supervisor force-editing a "
          + "Patrol Days platoon doesn't have to hunt through the "
          + "whole agency roster.",
      },
      {
        type: "improved",
        title: "Notification forwarding is now developer-only",
        detail:
          "The \"Notification forwarding\" panel in Settings → "
          + "Notifications is a debug tool for previewing another "
          + "user's notification stream — not something rank-and-"
          + "file or supervisors need to see. Gated to the platform "
          + "developer only; nobody else sees the section, matching "
          + "how Settings → Beta testers already works.",
      },
      {
        type: "new",
        title: "Shift Bid: Apply to schedule — pushes claimed rows into days-off patterns",
        detail:
          "L2+ can now finalize a shift bid. Once the sheet is "
          + "locked, an \"Apply to schedule\" button appears in the "
          + "header; confirm and the page walks every claimed row: "
          + "\n\n"
          + "• Splits the row's off_dates into week-1 / week-2 "
          + "weekday sets anchored to the sheet's start Sunday. Same "
          + "pattern both weeks collapses to a single-week rotation. "
          + "\n"
          + "• Moves the officer to the row's shift if it differs "
          + "from their current home (via the same "
          + "api.moveOfficerToShift the schedule uses) so historical "
          + "assignments follow. "
          + "\n"
          + "• Patches the employee row (daysOffWeekdays, "
          + "daysOffWeekdaysW2, daysOffStartDate, homeShiftId). "
          + "\n"
          + "• Materializes XXX rows across the next 365 days via "
          + "api.applyDaysOffPattern. Existing manual edits on "
          + "already-scheduled days are preserved. "
          + "\n\n"
          + "Settings → Shift Bid seeds its drafts from "
          + "employees.data, so once Apply finishes it auto-reloads "
          + "with the freshly-applied patterns — the older Settings "
          + "form remains the force-edit path for supervisors who "
          + "need to tweak week-1 patterns after the fact.",
      },
      {
        type: "new",
        title: "Shift Bid: lock / unlock a sheet to freeze bidding",
        detail:
          "L2+ can now lock a shift-bid sheet from the page header: "
          + "freezes claim / release / row-add / row-edit for "
          + "everyone until an L2+ unlocks it. Enforced three ways: "
          + "the claim + release RPCs reject when locked, a trigger "
          + "on shift_bid_rows rejects every insert / update / "
          + "delete when the parent sheet is locked (so nothing can "
          + "bypass the lock via a direct write), and the client "
          + "hides every action chip and disables the Edit toggle "
          + "so nobody's tempted to try. Header shows a \"Locked\" "
          + "chip with the lock timestamp on hover. Migration 0056.",
      },
      {
        type: "improved",
        title: "Shift Bid: drop Off column + Notify unclaimed officers (L2+)",
        detail:
          "Two touch-ups. The per-row \"Off\" count column is gone "
          + "(rows in a well-formed sheet all carry the same days-off "
          + "count so it was noise). New footer on each shift block "
          + "for L2+ once at least one row exists — shows how many "
          + "eligible officers for that shift haven't claimed yet and "
          + "a \"Notify unclaimed →\" button that fires a push + "
          + "email + inbox ping to each officer with a linked "
          + "account, inviting them to come pick.",
      },
      {
        type: "improved",
        title: "Shift Bid: eligibility highlights + bump notification + days-off count",
        detail:
          "Three polish additions to the shift-bid sheet. Each row "
          + "now shows a small \"Off\" column counting how many XXX "
          + "days the row carries so supervisors can build patterns "
          + "at a glance. Row backgrounds paint amber when the "
          + "signed-in officer is eligible to Claim or Bump the "
          + "row, and sky-blue for the row they hold — actionable "
          + "rows pop off the sheet without extra scanning. The "
          + "trailing action chip is now gated by eligibility: it "
          + "reads Claim / Bump → / Release / Held depending on the "
          + "signed-in officer's dept + shift + hire-date vs the "
          + "current claimant. And when a bump succeeds, the "
          + "displaced officer gets a push + email + inbox "
          + "notification (\"Shift Bid row taken\") so they know to "
          + "come pick a different row.",
      },
      {
        type: "new",
        title: "Shift Bid (beta): persisted sheets + officer claim with seniority bump",
        detail:
          "Fleshed out the Shift Bid page from the earlier shell. "
          + "Sheets and their rows now persist in a new pair of "
          + "tables (migration 0055): shift_bid_sheets keyed by "
          + "(department, start Sunday) and shift_bid_rows carrying "
          + "each row's shift, XXX day pattern, and claimant. Sheet "
          + "auto-materializes the first time a supervisor flips "
          + "Edit on for a given (dept, date). "
          + "\n\n"
          + "Officers pick their row via a Claim button in the "
          + "trailing column. Claim is routed through a "
          + "SECURITY DEFINER RPC that enforces the rules server-"
          + "side: same department + same home shift as the row, "
          + "and if the row is already held, the requester's "
          + "hire_date must be STRICTLY earlier than the current "
          + "claimant's — otherwise it's rejected as \"Row is held "
          + "by an officer of equal or greater seniority\". A more "
          + "senior officer sees a \"Bump →\" chip on a claimed "
          + "row; equal-or-junior officers get no button. "
          + "Claimants (and L2+) can release with a Release button.",
      },
      {
        type: "improved",
        title: "Records: drop the Shift column + sort by last name",
        detail:
          "The colored shift chips per row were visual noise once "
          + "the shift subdivision was already gone. Dropped the "
          + "Shift column entirely and sorted every dept table "
          + "purely by last name (then first name), so officers "
          + "read as one alphabetical list per department.",
      },
      {
        type: "improved",
        title: "Records: one table per department, no shift subdivision + no column totals",
        detail:
          "Records used to split each department into a sub-table "
          + "per home shift with a column-totals footer row. Flattened "
          + "to one table per department listing every officer in "
          + "that dept, with a small Shift column showing their home "
          + "shift as a colored chip. Rows still cluster by shift "
          + "order for readability. Column totals row dropped since "
          + "the per-officer Total column is what supervisors are "
          + "actually looking at.",
      },
      {
        type: "fixed",
        title: "Records: 20-hour double-count for full-shift OT (revised heuristic)",
        detail:
          "Follow-up to the earlier fix. The coverage_group_id "
          + "size check missed cases where two officers each cover "
          + "a full 12h half of a 24h shift (group size 2 but each "
          + "half is still a full replacement of home) — Bowen was "
          + "still reading 20h on some days. Replaced with a "
          + "simpler hours-based rule: if the officer's OT hours "
          + "on the day are >= their home shift's duration, treat "
          + "the OT as REPLACING home (skip home fallback + skip "
          + "any stray home-shift base row). Small stay-late / "
          + "come-early halves (OT < home duration) still layer "
          + "additively on top the way they should.",
      },
      {
        type: "improved",
        title: "Records: department card also shrinks to its tables' width",
        detail:
          "After the shift-block tightening the outer \"Patrol\" / "
          + "\"Dispatch\" / etc. dept card was still stretched across "
          + "the viewport, so the header bar sat over a wide blank "
          + "band with the shift tables narrow beneath. The dept "
          + "card now shrink-wraps to the width of its widest shift "
          + "table and centers as a unit — the whole department "
          + "reads as one compact block instead of a full-width "
          + "container with a centered inner strip.",
      },
      {
        type: "improved",
        title: "Records: shift header shrinks to the table's width",
        detail:
          "The colored shift bar (SCHOOL RESOURCE, DAYS, EVENINGS…) "
          + "used to stretch across the full viewport while the "
          + "table underneath was centered narrow — a wide colored "
          + "band with nothing under it. Wrapped the header + table "
          + "in a shared auto-width block that centers as a unit, "
          + "so the header now shrinks to match the table's natural "
          + "width and the whole shift group reads as one tight "
          + "block.",
      },
      {
        type: "improved",
        title: "Records: include on-leave officers + center the table",
        detail:
          "The roster filter was gating on status === 'active' and "
          + "silently dropping officers on 'leave'. Bond and anyone "
          + "else marked on leave now shows in Records like they do "
          + "on the schedule — only fully 'inactive' or deleted "
          + "officers are excluded. The tables also center in the "
          + "viewport now via mx-auto instead of pinning to the "
          + "left, so a narrow report doesn't have all its "
          + "whitespace on one side.",
      },
      {
        type: "improved",
        title: "Records: officers without a home shift no longer disappear + tighter table",
        detail:
          "Two Records tweaks. Officers with no home shift used to "
          + "show up only when their department_id matched a dept; "
          + "an owner / L1 whose account has no department_id at all "
          + "(Bond) fell through both filters and never appeared. "
          + "Grouping is rewritten so every active officer lands in "
          + "exactly one bucket: home shift → shift's dept + shift; "
          + "no home shift → dept's Unassigned section; neither → "
          + "a top-level \"Other\" bucket appended at the end. "
          + "Second, the table shrunk — cell padding, column widths, "
          + "and row heights all trimmed; the table no longer "
          + "stretches to fill its container so extra viewport width "
          + "reads as whitespace instead of inflated columns.",
      },
      {
        type: "new",
        title: "Records (beta): 2-week pay-period hours per officer",
        detail:
          "New \"Records\" section in the sidebar for L2+ (beta-"
          + "gated). Opens a page that lists every active officer "
          + "with their working hours per day + a running total for "
          + "a two-week pay period (Sun → Sat), grouped by "
          + "department → home shift so each shift reads as its own "
          + "block. A date picker at the top jumps to any pay "
          + "period (whatever date you pick snaps to that week's "
          + "Sunday); Previous / Today / Next step by 14 days. "
          + "\n\n"
          + "Hours are derived from the same signals the schedule "
          + "does: shift default duration, custom hour notes (\"9-"
          + "14\" → 5 hours), OT assignment rows (ot_start_time + "
          + "ot_end_time), off codes (coverage 0 → 0 hours), whole-"
          + "day time off (→ 0), partial time off (subtracts the "
          + "off window). BETA — verify against timekeeping before "
          + "payroll use.",
      },
      {
        type: "improved",
        title: "Schedule cell: amber highlight when a cell carries a note",
        detail:
          "A cell with any attached supervisor note now paints with "
          + "the same amber wash a partial-time-off cell does — one "
          + "shared \"there's context beyond the visible chip\" "
          + "signal. Double-click the cell to see the notes as chips "
          + "at the top of the picker.",
      },
      {
        type: "improved",
        title: "Code picker: custom-code input moved above the code list",
        detail:
          "The picker's custom-code input used to sit at the very "
          + "bottom, so autofocus on a hardware keyboard scrolled "
          + "the popover all the way down to reveal it and hid the "
          + "code list. Moved the input up between the officer "
          + "header and the code list. The picker now stays "
          + "scrolled near the top when opened, whether or not the "
          + "input takes focus.",
      },
      {
        type: "new",
        title: "Schedule cell: per-cell supervisor notes as chips in the picker",
        detail:
          "The code-picker now opens with a note chip stack at the "
          + "very top. Type into the \"Add a note…\" input and press "
          + "Enter (or click Add) to attach a free-form note to the "
          + "specific (officer, date) cell — as many as you need. "
          + "Each chip carries an × when you're the author or an "
          + "L2+ supervisor. Notes are visible to everyone in the "
          + "agency and persist in a new cell_notes table (migration "
          + "0054, own-row/agency RLS). Hidden in bulk-edit mode "
          + "since notes are per-cell.",
      },
      {
        type: "fixed",
        title: "Code picker always opens scrolled to the top",
        detail:
          "In some layouts the picker could open mid-code-list "
          + "instead of at the top, so the Officer + Date header + "
          + "Remove-code / time-off banner sat above the fold. "
          + "Added a useLayoutEffect that resets the popover's "
          + "scrollTop to 0 on every mount so the header is always "
          + "the first thing you see.",
      },
      {
        type: "fixed",
        title: "Schedule: clearing a cell no longer deletes the time-off record",
        detail:
          "Removing a chip from the schedule grid used to also "
          + "delete (or trim) the underlying approved time-off "
          + "request — a single-day request would vanish from the "
          + "Completed list along with its Time Off row. The "
          + "schedule now never mutates time_off records. The "
          + "picker's old \"Remove time off\" button is replaced "
          + "with a read-only amber banner (\"Approved time off · "
          + "Manage on Time Off →\") so a supervisor who actually "
          + "wants to cancel a request goes to the Time Off page "
          + "where the request + audit history stays intact. "
          + "Stamping a shift code over the cell still works and "
          + "still overrides the display for that day.",
      },
      {
        type: "improved",
        title: "Partial time-off cell now shows WORKED hours, not off hours",
        detail:
          "The schedule convention has always been to show hours "
          + "worked in a cell — a supervisor hand-typing a shortened "
          + "day writes \"08-13\", not \"13-16\". The new partial-"
          + "time-off overlay was accidentally showing the off "
          + "window (\"13:00-16:00 V\"), which read like the officer "
          + "was working 1-4pm. Fixed: the cell now computes the "
          + "worked range by subtracting the off window from the "
          + "shift and renders \"08-13 V\" instead. Middle-of-shift "
          + "partial (off 10-12 on an 08-16 shift) shows both worked "
          + "segments comma-joined: \"08-10,12-16 V\".",
      },
      {
        type: "improved",
        title: "Schedule cell: partial time-off details shown in the code picker",
        detail:
          "The hover tooltip that surfaced a partial time-off's type "
          + "+ hours + note was useless on mobile (no hover) and easy "
          + "to miss on desktop. Double-clicking the cell now opens "
          + "the usual code picker with an amber info banner at the "
          + "very top: the type code, the label (\"Partial vacation\"), "
          + "the hour window, and the requester's note. Sits above "
          + "the code list so a supervisor sees the context before "
          + "picking anything.",
      },
      {
        type: "new",
        title: "Time off: partial-day hours + schedule cell shows both statuses",
        detail:
          "Time-off requests can now cover part of a day instead "
          + "of the whole thing. The request form has a new \"Only "
          + "part of the day\" checkbox that reveals From / To time "
          + "inputs; submit and the row saves start_time + end_time "
          + "(migration 0053, single-day windows only, guarded by a "
          + "CHECK constraint). The Time Off review modal shows the "
          + "partial hours in the Dates row so reviewers see the "
          + "window before approving. "
          + "\n\n"
          + "On the schedule grid, a cell that has an approved "
          + "partial-day time-off overlapping a working assignment "
          + "now shows both statuses at once: an amber background "
          + "wash on the cell, a small chip inline with the "
          + "type code + the missing hour range (e.g. \"V 13:00-"
          + "16:00\"), and a hover tooltip that names the type and "
          + "time window. Whole-day time-off keeps the historical "
          + "full-cell chip behaviour. "
          + "\n\n"
          + "Coverage math subtracts the partial-off hours: an "
          + "officer with a 13-16 vacation on their 08-16 shift "
          + "contributes to buckets 8..12 as normal but drops from "
          + "13, 14, 15 — so the Understaffed rollup surfaces the "
          + "afternoon gap and a supervisor can call OT for it.",
      },
      {
        type: "improved",
        title: "Sidebar: custom forms slot in directly below Training",
        detail:
          "Custom forms used to render at the bottom of the sidebar "
          + "after On-Call, so a request form the user created sat "
          + "several rows away from Time Off and Training — its "
          + "built-in siblings. Custom forms now insert directly "
          + "after the Training row so all request-style forms live "
          + "together, in the order the agency created them.",
      },
      {
        type: "fixed",
        title: "Custom forms: sidebar icon + supervisor can submit on behalf of an officer",
        detail:
          "Two small fixes to the custom-forms surface. The sidebar "
          + "was rendering custom forms with a 📝 emoji that sat "
          + "oddly next to the ✦ used by Time Off and Training; "
          + "custom forms now use the same ✦ so the nav reads as "
          + "one family. And the \"+ New submission\" button no "
          + "longer stays disabled for supervisors whose own "
          + "account isn't linked to an employee record — when a "
          + "reviewer opens the modal, an employee picker at the "
          + "top lets them pick who they're submitting for "
          + "(defaults to the reviewer's own linked employee when "
          + "they have one). Rank-and-file officers still need "
          + "their own employee linked to submit for themselves.",
      },
      {
        type: "new",
        title: "Settings → Beta testers (developer-only): opt individual accounts into beta features",
        detail:
          "New profile.is_beta_tester flag (migration 0052) and a "
          + "Settings → Beta testers panel that only the platform "
          + "developer sees. Ticking an account grants beta-tester "
          + "access — the account keeps its existing level and "
          + "permissions, but sees any feature the developer has "
          + "gated to beta testers via canSeeBeta(me). Toggle "
          + "routes through a SECURITY DEFINER RPC that re-verifies "
          + "the caller is the developer server-side, so the panel "
          + "can't be bypassed by a client that renders it out of "
          + "policy.",
      },
      {
        type: "new",
        title: "Dashboard: employees can now request to cover a short shift (beta)",
        detail:
          "Understaffed rows on the dashboard picked up a compact "
          + "\"Request →\" button next to each shortage — visible "
          + "only to officers who would actually be eligible to "
          + "cover it (same-department for full coverage, adjacent-"
          + "shift for a split half, filtered out if they already "
          + "work the short shift or have approved time off that "
          + "day). The button opens a small dialog to pick full / "
          + "first-half / second-half and add an optional note. "
          + "\n\n"
          + "On submit the shift's department L2+ supervisors "
          + "receive a push + email + inbox notification (\"Coverage "
          + "request\") and a new Coverage-requests widget on their "
          + "dashboard shows the pending item. Clicking the item "
          + "opens a review dialog: requester name, shift, date, "
          + "requested coverage type, note, pre-filled OT start / "
          + "end hours, and a comment field. Approve creates the OT "
          + "assignment and pings the officer with a \"You've been "
          + "added to the schedule\" notification; Deny sends the "
          + "denial + comment. Requesters see a passive \"Requested\" "
          + "chip on rows they've already submitted so they don't "
          + "double up. Backed by migration 0051 (coverage_requests "
          + "table with RLS).",
      },
      {
        type: "new",
        title: "Schedule: 2-week / 4-week window toggle for smaller screens",
        detail:
          "New \"2wk / 4wk\" button in the schedule toolbar shrinks "
          + "the visible window from the standard 4-week pay-period "
          + "page down to a 2-week page, so the grid fits on laptop "
          + "displays without horizontal scroll. Previous / Today / "
          + "Next pageing follows the chosen width (2 weeks pages 14 "
          + "days at a time; 4 weeks stays at 28). The pay-period "
          + "deep-link snap uses the same width, and the print "
          + "dialog title updates to match. Persists per-device so a "
          + "supervisor who prefers 2-week mode on their MacBook and "
          + "4-week on the office monitor keeps each preference "
          + "where it belongs.",
      },
      {
        type: "new",
        title: "Layout: collapsible sidebar — hamburger-only mode for wide pages",
        detail:
          "The sidebar can now be collapsed on desktop the same way "
          + "it already collapses on mobile: a hamburger button in "
          + "the top-left opens the nav as a drawer, and closes it "
          + "with a tap outside. New \"Collapse sidebar\" strip at "
          + "the top of the sidebar toggles the mode; a second row "
          + "labelled \"Pin sidebar\" appears inside the drawer to "
          + "flip back. The wide pages that were getting cut off on "
          + "laptop screens (Schedule especially) get the full "
          + "viewport back. Preference persists per-device.",
      },
      {
        type: "fixed",
        title: "Dashboard: populated columns actually center now",
        detail:
          "The trailing empty column that lets you drag a widget "
          + "into a fresh column was rendered all the time — with "
          + "two widgets loaded that made the flex row lay out as "
          + "three columns and centered THAT, so the visible widgets "
          + "sat left-of-center with an invisible w-96 shell on the "
          + "right. Now the trailing empty column only appears while "
          + "a drag is actually in flight (toggled by the "
          + "DndContext's onDragStart / onDragEnd / onDragCancel). "
          + "Idle layout centers just the populated columns.",
      },
      {
        type: "fixed",
        title: "Schedule deep-links now snap to the pay-period page, not to column 0",
        detail:
          "Clicking a \"→ cell\" chip on the Audit page or an "
          + "overstaffed row on the dashboard used to open the "
          + "schedule with the target date pinned to the leftmost "
          + "column, which straddled two pay periods and broke the "
          + "usual pay-period grid alignment. The deep-link consumer "
          + "now runs the same math the \"jump to today\" effect "
          + "uses — floor the day-diff from the shared server "
          + "anchor into 28-day chunks — and lands on the pay-"
          + "period PAGE that contains the target date. So a jump "
          + "to 10-06 opens 09-30 → 10-27 with 10-06 sitting in its "
          + "natural mid-page column. Because the target date no "
          + "longer sits at column 0, every cell in the target "
          + "column now washes with a solid amber background that "
          + "fades to transparent over ~2.5 s — the whole column "
          + "reads as one lit-up strip instead of a stack of "
          + "individual bordered cells.",
      },
      {
        type: "improved",
        title: "Dashboard: overstaffed rows now jump straight to the schedule",
        detail:
          "Understaffed rows already opened the fix-coverage picker "
          + "on click. Overstaffed rows had no click behavior — the "
          + "supervisor saw \"Tuesday is +1\" and had to navigate to "
          + "the schedule and scroll to that date on their own. "
          + "Every overstaffed row is now a button that opens the "
          + "matching dept's schedule with ?anchor=<date>, so the "
          + "pay-period window slides to include that day the moment "
          + "the page opens. Works from both the per-dept widget "
          + "(L2 / L3 / L4) and the consolidated widget on the "
          + "Owner / L1 dashboard.",
      },
      {
        type: "fixed",
        title: "Dashboard: view-as no longer overwrites your saved layout",
        detail:
          "When you Preview As a different level, the dashboard "
          + "swaps the widget set (per-dept for L2 / L3 / L4, "
          + "consolidated for owner / L1). Any layout adjustments "
          + "you made during the preview used to save right back "
          + "to your own dashboard_prefs row, quietly replacing "
          + "your real arrangement with the preview's shape. Now "
          + "the debounced save is suppressed while previewLevel "
          + "is set, and returning to your own view rehydrates "
          + "state from the server — so a preview session leaves "
          + "your saved layout untouched.",
      },
      {
        type: "improved",
        title: "Dashboard: every column now uses the wider (24rem) width",
        detail:
          "Coverage widgets used to sit at 18rem while any column "
          + "holding the day-by-day schedule bumped to 24rem, so "
          + "swapping widgets between columns changed the widths "
          + "under you. All dashboard columns are now a uniform "
          + "24rem — the widgets are noticeably roomier and every "
          + "column matches its neighbours whatever's in it.",
      },
      {
        type: "fixed",
        title: "Dashboard: iOS scrolling no longer catches on widgets",
        detail:
          "Two touch-behavior fixes together: the widget wrapper "
          + "stayed pinned as `touch-none` even at rest, so every "
          + "finger drag over a widget was treated as a drag "
          + "gesture and blocked normal page scroll on iOS. Now "
          + "the wrapper is `touch-auto` until a drag actually "
          + "starts, then flips to `touch-none` mid-drag so the "
          + "gesture doesn't fight the browser. Second, the mobile "
          + "long-press to pick up a widget went from 500 ms to "
          + "1500 ms and its tolerance went from 5 px to 8 px — "
          + "any finger movement inside the hold window cancels "
          + "the activation, so a normal swipe just scrolls the "
          + "page without accidentally lifting a widget.",
      },
      {
        type: "new",
        title: "Dashboard: layout follows you across devices + auto-centers",
        detail:
          "Widget column arrangement and per-widget collapse state "
          + "now persist per user in a new dashboard_prefs table "
          + "(migration 0050) — log in on a different machine or "
          + "reinstall the app and your dashboard is exactly where "
          + "you left it. LocalStorage still provides the instant "
          + "first paint, then the server row hydrates over it 800 "
          + "ms after any change (debounced upsert). "
          + "Empty trailing columns no longer force the grid to "
          + "stretch across the full viewport: the dashboard only "
          + "renders columns up to your last populated one plus one "
          + "trailing drop zone, so two used columns center as two "
          + "columns instead of floating leftward next to two empty "
          + "shells.",
      },
      {
        type: "improved",
        title: "Dashboard: 14-day schedule widget renders in a wider column",
        detail:
          "The day-by-day schedule was cramped in the standard "
          + "18rem column — dates, labels and hour ranges kept "
          + "wrapping onto two lines. Any dashboard column that "
          + "contains a schedule widget now sizes up to a wider "
          + "24rem so the row content sits on one line. Other "
          + "columns keep the standard width; drag a schedule "
          + "widget into a column and the whole column grows to "
          + "match, drag it out and it shrinks back.",
      },
      {
        type: "improved",
        title: "Dashboard: L1 dashboard consolidates coverage widgets across depts",
        detail:
          "Owner and L1 dashboards used to render four widgets PER "
          + "department — with three departments that's twelve small "
          + "cards competing for the eye. Consolidated into four "
          + "widgets total: one Understaffed card, one Overstaffed "
          + "card, one Pending time off card, one Pending training "
          + "card. Each card has a dept-labelled section inside per "
          + "department that actually has data (empty depts silently "
          + "collapse). Same drag-anywhere columns and per-widget "
          + "collapse chevron from the last release — just fewer, "
          + "richer widgets. L2 / L3 / L4 dashboards are unchanged "
          + "since they only see their home dept.",
      },
      {
        type: "new",
        title: "Employees: FTO flag — trainees excluded from coverage math",
        detail:
          "Employee edit form (Settings → Employees) has a new "
          + "\"In training (FTO)\" checkbox alongside the OIC one. "
          + "When ticked, that officer stops counting toward shift "
          + "coverage and shortage — they're riding along with a "
          + "training officer and shouldn't inflate the seat count "
          + "for the pair. Their assignment row still shows on the "
          + "schedule grid; only the coverage rollup skips them. "
          + "A sky-blue FTO chip now sits next to the name on the "
          + "schedule grid rows and in the Fix-coverage picker, so "
          + "supervisors can see at a glance who isn't counting. "
          + "The employees table shows the same chip next to any "
          + "OIC chip, and the mass-edit dialog exposes the flag as "
          + "a bulk toggle so a whole training class can be flipped "
          + "in one click.",
      },
      {
        type: "new",
        title: "Dashboard: user-arrangeable 4-column grid + per-widget collapse",
        detail:
          "Widgets now live in a fixed 4-column desktop grid you "
          + "arrange yourself instead of an auto-balanced pool — drop "
          + "one widget on the left, three in the second column, one "
          + "in the third, four in the last, whatever suits the way "
          + "you scan the dashboard. Drag any widget across columns "
          + "and the layout persists per-device. Each widget carries "
          + "a collapse chevron in the top-right; collapsed widgets "
          + "shrink to a single-row header (dept dot + widget name + "
          + "dept name) so you can still see they're there without "
          + "losing space to their contents. Widgets whose content is "
          + "an empty state default to collapsed, non-empty widgets "
          + "default to expanded, and either default can be flipped "
          + "per widget. Mobile stays a single-column stack, still "
          + "drag-reorderable via long-press.",
      },
      {
        type: "improved",
        title: "Dashboard widget layout: masonry so shorter widgets stack next to a tall one",
        detail:
          "The flat widget pool switched from a rigid CSS grid "
          + "(same row height per column) to CSS columns. Shorter "
          + "widgets now stack vertically next to a tall one — you "
          + "can pile three or four coverage cards in the same "
          + "column as the 14-row schedule widget instead of leaving "
          + "huge blank space beside it. Each widget stays a self-"
          + "contained card thanks to break-inside-avoid, and the "
          + "drag-to-reorder pool still spans the entire dashboard.",
      },
      {
        type: "new",
        title: "Dashboard: cross-grid drag — all widgets in one flat pool",
        detail:
          "The schedule row and each department's coverage row have "
          + "dissolved into a single flat widget pool. Every widget "
          + "the dashboard shows (My schedule + any forwarded "
          + "schedules + each dept's Understaffed / Overstaffed / "
          + "Pending time off / Pending training) lives in one grid, "
          + "one DndContext, one SortableContext. Drag any widget "
          + "anywhere — including moving a coverage widget next to "
          + "your schedule, or interleaving different departments' "
          + "widgets — and the layout persists per-device via a "
          + "single localStorage key. Each dept-scoped widget keeps "
          + "its dept-colored left border and dept-name badge so "
          + "the origin is obvious even when everything's mixed "
          + "together. Per-dept training-awaiting-final banners and "
          + "custom-form pending strips render below the grid, one "
          + "block per dept, so the actionable review lists stay "
          + "grouped where you'd expect them.",
      },
      {
        type: "improved",
        title: "Custom-form notifications: L2 only pinged for their own dept",
        detail:
          "When an officer submits a custom form (Settings → Forms), "
          + "Owner and L1 supervisors are always pinged as before. "
          + "L2 supervisors are now filtered by home department — an "
          + "L2 with Dispatch as their home shift no longer receives "
          + "notifications for every Patrol submission. Time Off and "
          + "Training keep the existing agency-wide fan-out. The "
          + "filter is applied server-side in the send-notification "
          + "edge function; L2s without a linked employee record "
          + "silently drop out of the filtered set.",
      },
      {
        type: "improved",
        title: "Dashboard: color per DEPARTMENT + L2 locked to home dept",
        detail:
          "Widget accents now key off the department, not the widget "
          + "type — every Patrol widget carries the same colored "
          + "left-border + badge dot, every Dispatch widget another, "
          + "and so on. Colors come from a fixed palette assigned by "
          + "the department's sortOrder so they're stable across "
          + "sessions (sky, amber, emerald, purple, pink, indigo, "
          + "teal, orange rotating). Owner / L1 keep the multi-dept "
          + "view with all sections stacked. L2 / L3 / L4 are locked "
          + "to their home department — the department dropdown at "
          + "the top of the dashboard is gone; whichever dept an "
          + "officer belongs to is the one they see.",
      },
      {
        type: "improved",
        title: "Dashboard widgets: color accents + dept badges, smaller dept header",
        detail:
          "Each widget type now wears its own colored left border "
          + "and a small colored-dot dept badge at the top of every "
          + "dept-scoped widget: Understaffed (red), Overstaffed "
          + "(emerald), Pending time off (sky), Pending training "
          + "(purple). Schedule widgets stay neutral slate. The "
          + "dept-section header shrinks to a compact uppercase chip "
          + "with just the name + headcount, since each widget below "
          + "carries its own dept badge and the giant \"Patrol\" H2 "
          + "was competing with them for attention.",
      },
      {
        type: "new",
        title: "Dashboard widgets: drag to reorder (desktop + mobile)",
        detail:
          "Every widget in the schedule row and the per-dept "
          + "coverage row (Understaffed / Overstaffed / Pending time "
          + "off / Pending training) is now drag-to-reorder. On "
          + "desktop, press-and-drag any part of the widget (an 8 px "
          + "cursor move activates the drag, so a plain click still "
          + "hits buttons and links inside). On mobile, long-press "
          + "any part of the widget for half a second, then drag — "
          + "a normal tap still fires. A subtle top-edge grab strip "
          + "highlights on hover as an affordance. Layout persists "
          + "per-device via localStorage, and the coverage order is "
          + "applied uniformly across every department section so the "
          + "layout stays consistent. New widgets (e.g. a freshly-"
          + "forwarded schedule) land at the end of the saved order.",
      },
      {
        type: "improved",
        title: "My schedule widget: matches dashboard column width; silent days show home-shift hours",
        detail:
          "The My schedule (and forwarded schedule) widgets now sit "
          + "in a 4-across grid so they share the same column width "
          + "as the rest of the dashboard widgets instead of "
          + "spanning full-width on their own. And on any day where "
          + "there's no explicit assignment recorded yet, the widget "
          + "falls back to the officer's home shift's default hours "
          + "(e.g. Days = 06:00–14:00) instead of a dash — including "
          + "the tac-home-shift when the agency is in 12-hour mode "
          + "and the officer follows tac.",
      },
      {
        type: "new",
        title: "Notification forwarding — subscribe to another user's notifications",
        detail:
          "Settings → Notifications now has a Notification forwarding "
          + "panel. Pick a user and you'll also receive copies of "
          + "every notification sent to them — in the in-app inbox, "
          + "via email, and via push (both APNs and web push). "
          + "Forwarded rows show up with a \"[Fwd from Name]\" prefix "
          + "so the origin is obvious. Meant for owners testing their "
          + "own employee account without switching browsers, and for "
          + "supervisors who want to shadow-watch a specific officer. "
          + "RLS-scoped so you can only opt yourself IN to receive "
          + "someone else's notifications — no covert forwarding. "
          + "Dashboard adds an extra \"<Name> — next 2 weeks\" "
          + "schedule widget per forwarded user with an employee "
          + "record, so the owner sees Bond's schedule alongside their "
          + "own without leaving the page.",
      },
      {
        type: "new",
        title: "Dashboard: \"My schedule — next 2 weeks\" widget",
        detail:
          "Replaces the \"My next two weeks\" card. One row per "
          + "calendar day for the next 14 days: date + weekday, time "
          + "range (or \"Day off\" / the time-off type / a dash when "
          + "nothing's scheduled), and the total hours for the day "
          + "on the right. OT rows carry a small amber OT badge and "
          + "show the OT hours specifically. Approved time-off (any "
          + "type) wins over the schedule grid for the affected days, "
          + "so a vacation reads as \"Vacation\" not the base-shift "
          + "hours.",
      },
      {
        type: "fixed",
        title: "Forms: \"+ New submission\" button + submit modal",
        detail:
          "The Forms page (Settings → Forms → each custom form's "
          + "page) had no visible way to start a submission — the "
          + "submit card only rendered for accounts with an employee "
          + "record and was easy to miss. Now every custom form's "
          + "page has a prominent + New submission button in its "
          + "header that opens a modal with the form's fields + a "
          + "note field. Accounts not linked to an employee record "
          + "see the button disabled with a tooltip explaining why.",
      },
      {
        type: "new",
        title: "Custom forms are now submittable end-to-end",
        detail:
          "Follow-up to the Forms feature — custom forms defined in "
          + "Settings → Forms now have a working submission + review "
          + "flow. Toggling one on in Sections & display adds a "
          + "sidebar link to /forms/<slug> (with a 📝 icon). Officers "
          + "fill out the custom fields, hit Submit, and every "
          + "supervisor gets a notification. Supervisors approve or "
          + "deny inline on the same page and the submitter gets a "
          + "notification back with the status. Pending submissions "
          + "also surface on the Dashboard under each department "
          + "section as a \"Pending form submissions\" widget "
          + "(Owner / L1 see all departments, L2 sees their home "
          + "dept, L3 / L4 see their own submissions) so nothing "
          + "gets missed. Data-wise submissions ride on the existing "
          + "time_off table with a new form_slug column, so the "
          + "approval + notification wiring is shared with Time Off "
          + "and Training.",
      },
      {
        type: "new",
        title: "Settings → Forms: create custom request forms",
        detail:
          "Renamed the Request fields section to Forms and rebuilt "
          + "it around one collapsible card per form. Time off and "
          + "Training show as built-in cards — you can edit their "
          + "fields here but not delete the forms themselves (toggle "
          + "them off in Sections & display if you don't want them). "
          + "Hit + New form to create your own — pick a name, review "
          + "the auto-derived slug, and the new form appears with "
          + "its own field catalogue AND as a toggleable row in "
          + "Sections & display so you can turn it on and off. "
          + "Custom forms are deletable with a two-click confirm; "
          + "deleting one also removes its field definitions but "
          + "keeps any values already saved on past requests. "
          + "Submitting to a custom form isn't wired up yet — the "
          + "form definitions are ready to hook a submission page "
          + "onto in the next step.",
      },
      {
        type: "improved",
        title: "Dashboard: KPI row gone, employee count moved beside the dept name",
        detail:
          "Dropped the 4-KPI row (Employees / Shifts configured / On "
          + "schedule today / Pending time off) from every department "
          + "section. In its place the section header now shows the "
          + "dept name on the left and a muted \"N Employees\" on the "
          + "right of the same line — same info at a glance without "
          + "the whole KPI strip. Page title simplifies to just "
          + "\"Dashboard\" (was \"<Dept> dashboard\") since each "
          + "section carries its own dept label now.",
      },
      {
        type: "new",
        title: "Dashboard: per-department sections for Owner / L1",
        detail:
          "Owner and L1 accounts now see one full section (KPI row + "
          + "widgets row + any Training-awaiting-final callout) per "
          + "department, stacked with the dept name above each. No "
          + "dropdown at the top for these accounts — every department "
          + "is shown at once so nothing is hidden behind a picker. "
          + "L2 / L3 / L4 keep the single-dept view via the dropdown "
          + "and default to the signed-in user's home department, "
          + "same as before. Shifts + coverage are fetched once "
          + "agency-wide and each department section filters its own "
          + "slice out of the shared pool, so N departments don't "
          + "mean N × the round-trips.",
      },
      {
        type: "improved",
        title: "Dashboard: pending requests split into two cards",
        detail:
          "The single Pending requests card is now two: Pending time "
          + "off (third widget) and Pending training (fourth widget) "
          + "— one for each request type. Each card links straight "
          + "to its own review page. Time-off rows show the short "
          + "type code chip (V, S, PL, …); training rows keep the "
          + "amber \"L2 approved, awaiting final\" note when a "
          + "request is mid-two-step-approval. Fills the placeholder "
          + "slot in the 4-across dashboard row so nothing is "
          + "reserved-for-future anymore.",
      },
      {
        type: "improved",
        title: "Dashboard: Understaffed / Overstaffed locked to 30-day window",
        detail:
          "Dropped the 7d / 2w / month toggle from the Understaffed "
          + "shifts widget — both coverage widgets now always show "
          + "the upcoming 30 days. Also removed the \"Extra OT — "
          + "review and remove\" caption from the Overstaffed header "
          + "so it matches the tighter Understaffed layout. Pending "
          + "time off KPI label bumped from (7d) to (30d) to match "
          + "the shared window.",
      },
      {
        type: "improved",
        title: "Dashboard: coverage row matches the KPI row's 4-across grid",
        detail:
          "Understaffed shifts, Overstaffed shifts, and Pending "
          + "requests now sit on a single 4-column row directly under "
          + "the KPIs — each card the same width as one KPI card. "
          + "The fourth slot is a dashed \"Reserved for a future "
          + "widget\" placeholder for now; a new widget will fill it "
          + "later. On narrow screens all four cards reflow to 2-col, "
          + "same as the KPI row.",
      },
      {
        type: "new",
        title: "Dashboard: Upcoming overstaffed shifts panel",
        detail:
          "Companion to Understaffed shifts. Lists every (shift, "
          + "date) in the active department where the shift is over "
          + "its minimum staffing AND has at least one OT chip on it "
          + "— matching the same philosophy behind the green OT chip "
          + "on the Schedule (natural over-staffing without OT stays "
          + "excluded). Same grouping and colouring pattern as the "
          + "Understaffed panel; only renders when there's something "
          + "to review so the dashboard stays quiet on a normal day.",
      },
      {
        type: "new",
        title: "OT chips light up green when the shift is overstaffed",
        detail:
          "If a shift is over its minimum staffing on a date and one "
          + "or more OT chips are on that date, every OT chip on that "
          + "(shift, date) flips from amber to bright green — mirror "
          + "of how the Coverage row goes red when short. Signal is "
          + "\"one of these OT chips can probably come out\"; the "
          + "supervisor decides which. Natural over-staffing (no OT "
          + "involved) stays as-is. The green flag still fires even "
          + "when the coverage cell is under a supervisor override "
          + "so nothing gets silently forgotten under one.",
      },
      {
        type: "new",
        title: "Sortable columns on the Employees page",
        detail:
          "Click any header on the Employees table — Name, Badge #, "
          + "Radio #, Rank, Status, OIC, Level, Home shift, 12-hour "
          + "home shift — to sort the roster by that column. Click the "
          + "same header again to flip between ascending and "
          + "descending. Empty cells always sink to the bottom either "
          + "way, so a page of \"—\" values can't push the officers "
          + "you care about below the fold. Badge and Radio compare "
          + "numerically when both cells are numbers, and Level "
          + "sorts by seniority (Owner → L4) rather than "
          + "alphabetically. An arrow next to the active header "
          + "shows which direction is currently in effect.",
      },
      {
        type: "improved",
        title: "Dashboard swaps Recent activity for Pending requests",
        detail:
          "The Recent activity feed (a scrolling audit log) is gone "
          + "from the Dashboard — supervisors who want that view can "
          + "still open the full Audit page from the sidebar. In its "
          + "place is a Pending requests panel: for Owner / L1 / L2 "
          + "it lists every still-open time-off + training request in "
          + "the active department (with the requester's name, type "
          + "badge, date range, and an \"L2 approved, awaiting final\" "
          + "note on training that's mid-two-step-approval); for L3 / "
          + "L4 it shows only the signed-in user's own pending "
          + "requests. Header links jump straight to the Time Off or "
          + "Training page for the actual review action.",
      },
      {
        type: "improved",
        title: "Agency image moved up next to the ShiftManager header",
        detail:
          "The uploaded agency image used to sit as a big square at "
          + "the very bottom of the sidebar, past every menu item. "
          + "It now shows as a compact 40×40 app-icon-style square "
          + "immediately to the left of the \"ShiftManager · build "
          + "XXX\" banner at the top of the sidebar — same spot on "
          + "desktop and the mobile drawer. Nothing else changed "
          + "about the image itself; the Owner-only upload / replace "
          + "/ remove controls still live in Settings → Agency.",
      },
      {
        type: "new",
        title: "Change your password from the Settings → Account tab",
        detail:
          "Every officer now has a Change password card at the top of "
          + "the Account section in Settings. Enter your current "
          + "password (verified server-side to prove it's you), then "
          + "your new one twice. New password must be at least 8 "
          + "characters and different from the old. The session stays "
          + "signed in — no need to log back in on this device. If "
          + "you don't remember your current password there's a "
          + "\"Email me a reset link\" button on the same card that "
          + "sends a magic link to the address on file; the sign-in "
          + "page has always had the same \"Forgot password?\" flow "
          + "for locked-out users.",
      },
      {
        type: "improved",
        title: "Preview/production channel switcher moved into the sidebar",
        detail:
          "The platform developer's promote-to-production and "
          + "switch-channels chip used to float in the top-right "
          + "corner of every page, covering whatever content was "
          + "there. It now lives inside the sidebar as a compact "
          + "amber (preview) or sky (live) block right under the "
          + "\"ShiftManager · build XXX\" header, out of the way of "
          + "page content. Non-developers still see nothing.",
      },
      {
        type: "fixed",
        title: "Shift bid table no longer hides Saturday under the scrollbar",
        detail:
          "On the Shift bid editor and the Departments / Shift codes / "
          + "Request fields lists, the vertical scrollbar was sitting "
          + "on top of the rightmost column and clipping its contents "
          + "(most visibly the Saturday day-off checkbox on Shift "
          + "bid). Added scrollbar-gutter: stable AND reserved 12 px "
          + "of right padding inside each scrollable table so the "
          + "scrollbar overlay always has its own dead space to sit "
          + "in — the Saturday buttons and the Actions column now "
          + "stay fully visible on every browser.",
      },
      {
        type: "improved",
        title: "Shift bid opens inline in Settings, no more modal window",
        detail:
          "The Shift bid section used to show a placeholder page with "
          + "an \"Open shift bid\" button that popped a modal — one "
          + "extra click and a cramped modal-width layout that forced "
          + "the officer table to scroll sideways even on a wide "
          + "screen. Selecting Shift bid in the Settings sidebar / "
          + "accordion now renders the full editor directly on the "
          + "page, using the panel's full width so the officer + shift "
          + "+ 7-day columns fit without any horizontal scrollbar "
          + "when there's room, and only scroll sideways on narrow "
          + "screens where they truly can't fit.",
      },
      {
        type: "improved",
        title: "Settings page reorganized into a sidebar-navigable layout",
        detail:
          "The Settings page used to be a two-column grid where every "
          + "panel scrolled past at once — Agency, Departments, "
          + "Shift codes, Backups, Subscription, Notifications, and "
          + "the rest all stacked together. Now there's a sidebar on "
          + "the left listing each section, and only the section you "
          + "click shows in the main pane. On phones and the iOS app "
          + "the sidebar folds into an accordion — tap a section name "
          + "to expand its panels below, and the previously-open "
          + "section collapses. Sections are grouped so related "
          + "toggles live together (Sections & display holds enabled "
          + "features + pay-period markers + row order + beta "
          + "features; Notifications holds web push + the test "
          + "sender; Account holds delete + deletion-request review). "
          + "Long lists (Departments, Shift codes, Request fields) "
          + "now grow to fill the viewport before their inner "
          + "scrollbar appears, instead of being capped at ~4 rows. "
          + "Deep links keep working — the sidebar's \"+ New "
          + "department\" link and old #departments URLs jump straight "
          + "into the Departments section.",
      },
      {
        type: "improved",
        title: "Schedule reuses cached data instead of refetching for coverage",
        detail:
          "api.coverage was refetching shifts, employees, agency, "
          + "assignments, shift-codes, and time-off every mount even "
          + "though the Schedule page already had them cached via its "
          + "own useQuery hooks. Now the client hands those in and "
          + "api.coverage only fetches what's missing (typically just "
          + "coverage_overrides). Cuts the load from ~7 round-trips "
          + "to 1 on any mount after the initial page load.",
      },
      {
        type: "improved",
        title: "Schedule loads noticeably faster — coverage queries now run in parallel",
        detail:
          "The coverage compute was firing seven Supabase requests "
          + "sequentially every time the Schedule mounted (shifts + "
          + "employees + agency + assignments + shift-codes + time-"
          + "off + overrides), so on top of the network round-trip "
          + "it added several seconds of dead time before the "
          + "officers and coverage numbers could appear. All seven "
          + "now fire in parallel via Promise.all — same requests, "
          + "one round-trip.",
      },
      {
        type: "improved",
        title: "Schedule loads faster on agencies with lots of assignments",
        detail:
          "Coverage math now pre-indexes assignments by date and "
          + "shifts by id so the per-(shift, date) roster + OT + "
          + "spillover loops don't re-scan the whole month of "
          + "assignments once per cell. Same numbers, fewer wasted "
          + "iterations.",
      },
      {
        type: "improved",
        title: "OT chip pairing is now a Yes/No prompt after Fix Coverage saves",
        detail:
          "Removed the silent auto-join that was chaining unrelated "
          + "OT rows into one chip. Instead, after you save a Full "
          + "or Cross OT and a solo sibling on the same cell has "
          + "hours that don't overlap yours, Fix Coverage now asks: "
          + "'Attach as one paired coverage?' with Yes / No buttons. "
          + "Yes paints the two as one paired chip, No leaves them "
          + "as separate solos. Same-hours OTs (both 06-18) always "
          + "stay separate. Split coverages already create their "
          + "own matched pair and never trigger the prompt.",
      },
      {
        type: "new",
        title: "Unassigned officers pickable for partial coverage",
        detail:
          "Fix Coverage's Split OT tab now shows officers without a "
          + "home shift (fill-ins, reserves) under an 'other officers' "
          + "section on both the first-half and second-half slots — "
          + "so they can be assigned to cover just a portion of a "
          + "gap the same way rostered officers can. Full OT already "
          + "included them.",
      },
      {
        type: "fixed",
        title: "OT chip no longer chains 4 or more officers into one label",
        detail:
          "The auto-join that pairs complementary halves was "
          + "chaining every successive OT on the same cell into an "
          + "ever-growing group, so a third or fourth supervisor "
          + "add landed as \"A-B-C-D\" in one chip. Auto-join now "
          + "only fires when the sibling is currently solo — the "
          + "third and later adds start fresh chips of their own.",
      },
      {
        type: "new",
        title: "Turn optional sections on / off per agency",
        detail:
          "Settings has a new Enabled sections panel (L1+ only). "
          + "Untick Time Off, Training, Audit, or On-Call to hide "
          + "it from the sidebar and iOS menu for every user in the "
          + "agency. The route itself redirects to Dashboard so a "
          + "bookmarked URL to a turned-off section can't reach it "
          + "either. Nothing is deleted — flip a section back on any "
          + "time to restore. Employees, Shifts, Schedule, and "
          + "Dashboard always stay on.",
      },
      {
        type: "new",
        title: "Soft OT catch when supervisors type hours past a shift's end",
        detail:
          "If a supervisor types hours like \"6-18\" into a Days "
          + "officer's cell, the schedule now spots that the 14-18 "
          + "portion is really Evenings OT and floats a yellow "
          + "banner offering to record it as coverage on Evenings "
          + "with one click. Say No and the typed note stays as it "
          + "was; say Yes and the OT gets stamped onto the right "
          + "shift so it shows up in the OT row and the coverage "
          + "count — no more OT hours quietly disappearing because "
          + "they were typed instead of picked through Fix Coverage.",
      },
      {
        type: "fixed",
        title: "ACO and Dispatch coverage inflated by unrelated Patrol OT",
        detail:
          "Specialty shifts like Animal Control (08-17) and "
          + "Dispatch (24-hour) share clock hours with Patrol Days/"
          + "Evenings/Nights, so the cross-shift OT hour spillover "
          + "was spuriously crediting every Patrol OT chip whose "
          + "hours happened to overlap — an ACO shift with two "
          + "officers could read 7/1 or 8/1 as Patrol pairs stacked "
          + "up. Spillover now only fires within the same rotation "
          + "family (same department AND both shifts in_rotation), "
          + "so ACO stays capped at its actual officers.",
      },
      {
        type: "fixed",
        title: "Coverage numbers inflated to counts like 7/1 after paired OT",
        detail:
          "The cross-shift OT spillover I added earlier was double-"
          + "counting officers on their own home shift when their "
          + "OT hours also overlapped their home shift window — "
          + "so a shift needing 1 body could read 7/1 once several "
          + "extended-hours OT pairs stacked up on adjacent shifts. "
          + "Coverage math now dedupes credits per officer per hour "
          + "bucket, so each body only counts once for each hour "
          + "they're at work.",
      },
      {
        type: "improved",
        title: "Dark mode: opaque surfaces and sharper borders",
        detail:
          "Dark mode swaps the translucent accent tints (OT amber, "
          + "error red, info sky, success emerald) for solid, low-"
          + "saturation fills, and swaps translucent borders for "
          + "opaque steel lines. The body ground goes a hair cooler "
          + "and deeper, cards read as distinct objects instead of "
          + "ghosted onto the background, and the OT chip shifts to "
          + "a yellower gold so it doesn't lean into the red family. "
          + "No layout, font, or spacing changes.",
      },
      {
        type: "improved",
        title: "Go to cell chip now appears on coverage-override audit entries",
        detail:
          "When a supervisor uses Mark covered (override) on Fix "
          + "Coverage or removes an existing override, the audit "
          + "log's → cell chip now shows on that entry too — "
          + "click it to hop straight to the schedule with the "
          + "affected shift's date in view.",
      },
      {
        type: "improved",
        title: "Independent OT halves now render as one paired chip",
        detail:
          "When one supervisor picks the first half of a coverage "
          + "gap and a second supervisor later fills the remaining "
          + "half, the two rows now automatically join under a "
          + "single coverage group — so the schedule shows them as "
          + "one paired 'X-Y' chip instead of two separate ones. "
          + "Auto-joins only when the second half's hours don't "
          + "overlap the first's, so a genuinely-doubled seat "
          + "stays as two independent chips.",
      },
      {
        type: "fixed",
        title: "OT hours that span multiple shifts now count on every shift they cover",
        detail:
          "An officer covering \"06:00-18:00\" on Evenings is "
          + "physically at work during all of Days (06-14) and the "
          + "first four hours of Evenings (14-18). Before, the OT "
          + "row was only credited to the shift it was filed on, so "
          + "Days would still read short even though a body was "
          + "actually there — and paired with someone else running "
          + "\"18:00-06:00\", Nights would show short too. Coverage "
          + "now credits every shift whose hours overlap the OT's "
          + "worked window.",
      },
      {
        type: "new",
        title: "L2+ can force-save a code past a validation error",
        detail:
          "When the picker throws a red 'Fix and try again' banner, "
          + "L2 supervisors and up (Owner, L1, L2) now see a Force "
          + "save anyway button in that banner. Clicking it stamps "
          + "the code into the cell even if the OT-elsewhere or "
          + "approved-leave guard would normally reject it. The "
          + "resulting chip renders in BOLD RED so the override is "
          + "obvious at a glance, and reopening the picker shows "
          + "Revert override in place of the usual Remove code — "
          + "one click undoes it. Nothing else about the cell "
          + "changes; OT rows, time-off approvals, and coverage "
          + "math are all untouched.",
      },
      {
        type: "fixed",
        title: "Cell edits failing silently when officer had OT on a different shift",
        detail:
          "Marking an officer sick / off on their normal shift was "
          + "sometimes 'doing nothing' when the officer already had "
          + "an OT record for the same date on a different shift "
          + "(Nights OT, cross-department, etc.). The underlying "
          + "same-day guard was counting across every shift instead "
          + "of just the shift being edited, and the mutation was "
          + "swallowing the resulting error. Now the guard only "
          + "trips on a real same-shift duplicate, and any cell-"
          + "edit failure surfaces in the top red banner so you "
          + "know exactly what the app rejected and why.",
      },
      {
        type: "fixed",
        title: "Sick / off codes wrongly blocked when OT was on a different day",
        detail:
          "Marking an officer sick (or off with any other zero-"
          + "coverage code) on their normal shift day used to fail "
          + "with 'covering overtime on that date' whenever they "
          + "had an OT record whose worked hours actually landed "
          + "on the NEXT calendar day — the common wrap-midnight "
          + "'second half of Nights' case. The check now uses the "
          + "chip's real render day (the day the officer is "
          + "physically at work) so Cain being sick on 9/15 no "
          + "longer conflicts with his 02:00-14:00 Nights OT that "
          + "is worked on 9/16.",
      },
      {
        type: "fixed",
        title: "iOS app was frozen / unresponsive after the update",
        detail:
          "The developer preview/stable banner has been suppressed "
          + "on the native iOS build. It doesn't apply to the App "
          + "Store version anyway (iOS ships through TestFlight, "
          + "not the /preview/ URL) and its invisible top edge was "
          + "sitting behind the Dynamic Island and intercepting "
          + "every tap.",
      },
      {
        type: "improved",
        title: "Developer channel banner now shows on both preview and production",
        detail:
          "The floating chip in the top-right (visible only to the "
          + "platform developer account) is now symmetric: on "
          + "production it says 'Live production build' and offers "
          + "a Switch to preview button; on preview it stays "
          + "amber with the Promote to production button. Makes "
          + "hopping between channels during testing a single "
          + "click either direction.",
      },
    ],
  },
  {
    sha: "cbacc9e",
    date: "2026-08-22",
    changes: [
      {
        type: "improved",
        title: "Changelog now groups by production build, not by day",
        detail:
          "Every entry is one release: build number + the date it "
          + "was promoted to production. Multiple releases on the "
          + "same day each get their own block. Work that's on "
          + "preview but not yet promoted shows under a Next "
          + "release header so you can see what's queued before "
          + "clicking Promote.",
      },
      {
        type: "improved",
        title: "Validation errors stay in the picker instead of the top banner",
        detail:
          "A bad code or hour range now shows a red banner inside "
          + "the code picker or the inline editor and keeps the "
          + "form open so you can fix it in place. No more chasing "
          + "the top-of-page error while the picker slams shut.",
      },
    ],
  },
  {
    sha: "2c33c7c",
    date: "2026-08-22",
    changes: [
      {
        type: "improved",
        title: "Cell entries stay clean: 24-hour format enforced",
        detail:
          "Typing hours into a cell now forces 24-hour format. "
          + "\"9-14\" auto-pads to \"09-14\", \"22-06\" is accepted "
          + "for wrap-midnight ranges, and bad entries like \"5\" "
          + "(a bare number), \"9-5\" written for 9AM-5PM, or "
          + "\"2p\" are rejected so garbage never sticks. Letter-"
          + "first entries (SA, XXX, V, TR, OT½, etc.) still work "
          + "exactly the same — code first, hours second, no "
          + "confusion.",
      },
      {
        type: "new",
        title: "Per-day coverage counting for shifts like Animal Control",
        detail:
          "New toggle on the Shift edit form: 'Count coverage per day, "
          + "not per hour.' Turn it on for shifts where a single "
          + "officer working their own hours fully covers the day — "
          + "two ACOs on 8-4 / 9-5 with staggered days off, for "
          + "example. The shift now reads as covered whenever at "
          + "least min-staffing officers have a working assignment "
          + "that date, no matter which hours they cover. Off by "
          + "default so every other shift keeps the normal hourly-"
          + "bucket math.",
      },
      {
        type: "new",
        title: "Jump from an audit entry back to the schedule cell",
        detail:
          "Each assignment row in the Audit log now has a → cell "
          + "chip. Click it to hop straight to the Schedule with "
          + "the affected cell selected, the pay period scrolled to "
          + "that date, and a brief amber pulse so your eye lands "
          + "on the cell. Pairs with 'View change history for this "
          + "cell' so you can move both directions between the "
          + "Schedule and the log.",
      },
      {
        type: "new",
        title: "See the change history for any schedule cell",
        detail:
          "Double-click a cell to open the code picker, then click "
          + "'View change history for this cell' at the bottom. "
          + "Opens the Audit log filtered to every create, update, "
          + "and delete that ever touched that specific "
          + "(officer, date, shift) — going back to when the audit "
          + "log first started, not just the last 500 entries. "
          + "Only visible to users with audit access.",
      },
      {
        type: "new",
        title: "Fix Coverage auto-fills to the actual uncovered hours",
        detail:
          "When you open Fix Coverage on a short shift — including "
          + "when an officer has partial hours in their cell like "
          + "\"6-12\" on a 6-14 shift — the modal now shows the "
          + "uncovered window (e.g. Uncovered: 12:00–14:00) and "
          + "pre-fills the OT hours picker to that gap. Pick the "
          + "covering officer and click Assign OT; no more hand-"
          + "typing the leftover hours.",
      },
      {
        type: "new",
        title: "Preview builds for testing before going live",
        detail:
          "Updates now land at pdshiftmanager.com/preview/ first. "
          + "The platform developer sees a preview banner with a "
          + "Promote to production button — nothing hits the live "
          + "site until it's pushed there.",
      },
      {
        type: "new",
        title: "Click the build number to see what's changed",
        detail:
          "The small build code in the sidebar footer now opens "
          + "this changelog. Every release lists what's new, what "
          + "was improved, and what was fixed.",
      },
      {
        type: "fixed",
        title: "Overtime chip appearing on the wrong day for split coverage",
        detail:
          "When a shift is covered by two officers — one from the "
          + "shift before, one from the shift after — the chip now "
          + "lands on the same calendar day each officer's own home "
          + "shift is scheduled for. Previously the wrap-midnight "
          + "cases (Night covering the first half of Day, Day "
          + "covering the second half of Night) landed on the wrong "
          + "row.",
      },
      {
        type: "fixed",
        title: "Overtime chip wouldn't clear when deleted from the officer's row",
        detail:
          "Clearing an overtime chip in an officer's home row "
          + "(Backspace or Remove code) now actually deletes the "
          + "underlying overtime assignment — even when that "
          + "assignment lives on a different shift.",
      },
      {
        type: "improved",
        title: "Audit log inquiries beyond the last 500 rows",
        detail:
          "Added From / To date filters and a Load older button so "
          + "you can pull audit records from any point in the past. "
          + "Nothing has ever been aged out of the log — this just "
          + "makes older activity reachable from the UI.",
      },
    ],
  },
];
